import config from '@/config';
import * as _ from 'lodash-es';
import logger from '@/app/logger';

const FEATURE_GROUPS = config.FEATURE_GROUPS;

function getVirtualPresentation(links: any): any {
  if (links) {
    const vrLink = links.find((link: any) => link.linkType === 'virtualPresentation');
    return vrLink && vrLink.url.indexOf('matterport') !== -1 ? vrLink : null;
  }
  return null;
}

function getBluePrintPicture(images: any): any {
  return images.find((img: any) => img.subType === 'layoutPicture');
}

function sortImages(images: any[], virtualPresentation: any): any[] {
  const imagesList = images.filter((img: any) => img.subType !== 'layoutPicture');
  const bluePrintPicture = getBluePrintPicture(images);

  // Virtual presentation as second picture
  if (virtualPresentation) {
    imagesList.splice(1, 0, virtualPresentation);
  }

  // Blueprint as third picture
  if (bluePrintPicture) {
    imagesList.splice(2, 0, bluePrintPicture);
  }

  return imagesList;
}

function getListingTypeById(id: any) {
  return config.LISTING_TYPES[id as keyof typeof config.LISTING_TYPES];
}

function isListingTypeProperty(type: string) {
  return !!type && type.toUpperCase() === config.COMMISSION_TYPES.Property.toUpperCase();
}

function getPrimaryAreaWithUnits(listingType: any, livingArea: any, totalArea: any, siteArea: any) {
  if (!listingType || !listingType.primaryArea) {
    logger.error('listingType should have primaryArea defined, was:', { listingType });
    return null;
  }

  let area;
  if (listingType.primaryArea === 'siteArea') {
    if (siteArea && siteArea.size) {
      area = siteArea;
      area.isSiteArea = true;
    }
  } else {
    if (livingArea && livingArea.size) {
      area = livingArea;
    } else if (totalArea && totalArea.size) {
      area = totalArea;
    }
  }
  return area;
}

function getPrimarySalesPrice(pricing: any) {
  return hasDebtFreePrice(pricing) ? pricing.debtFreePrice : pricing.price;
}

function hasDebtFreePrice(pricing: any) {
  return pricing.debtFreePrice && pricing.debtFreePrice !== pricing.price;
}

function isFarm(featureGroups: any) {
  if (!_.isArray(featureGroups)) return false;
  return featureGroups.includes(FEATURE_GROUPS.FARMS.id);
}

function isSite(featureGroups: any) {
  if (!_.isArray(featureGroups)) return false;
  return featureGroups.includes(FEATURE_GROUPS.SITES.id);
}

function isOffice(featureGroups: any) {
  if (!_.isArray(featureGroups)) return false;
  return featureGroups.includes(FEATURE_GROUPS.OFFICES.id);
}

/**
 * Returns query parameters for similar/suggested listings.
 * @param listing Listing for which to query suggestions.
 */
function getQueryParams(listing: any) {
  const params: any = {
    limit: 5, // 5 to get max. 4 as the listing being viewed is removed
    order: 'desc',
    orderBy: 'created',
    group: listing.baseInfo.listingGroup,
    type: listing.baseInfo.listingType,
  };
  if (listing.baseInfo.location.districtId) {
    params.districtId = listing.baseInfo.location.districtId;
  } else if (listing.baseInfo.location.regionId) {
    params.regionId = listing.baseInfo.location.regionId;
  } else if (listing.baseInfo.location.cityId) {
    params.cityId = listing.baseInfo.location.cityId;
  } else if (listing.baseInfo.location.postalCode) {
    params.postalCode = listing.baseInfo.location.postalCode;
  }
  return params;
}

export default {
  getListingTypeById,
  getPrimaryAreaWithUnits,
  getPrimarySalesPrice,
  hasDebtFreePrice,
  isFarm,
  isSite,
  isOffice,
  getQueryParams,
  isListingTypeProperty,
  getVirtualPresentation,
  sortImages,
  getBluePrintPicture,
};
